import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { useAuth } from "@/hooks/useAuth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard,
  LogOut,
  Bookmark,
  Shield,
  FileText,
  FolderTree,
  MessageSquare,
  Mail,
  Send,
  Home,
  User,
} from "lucide-react";
import { ReadingHistoryDrawer } from "./ReadingHistoryDrawer";

interface AccountMenuProps {
  variant?: "desktop" | "mobile";
  /**
   * When true, render the menu items tailored for the admin dashboard
   * (deep links into admin tabs + "View Site"). Otherwise render the
   * public-site menu (Home, Bookmarks, plus "Admin Dashboard" for admins).
   */
  context?: "site" | "admin";
}

export function AccountMenu({
  variant = "desktop",
  context = "site",
}: AccountMenuProps) {
  const { user, isAdmin, signOut } = useAuth();
  const navigate = useNavigate();
  const [imgError, setImgError] = useState(false);

  if (!user) {
    return (
      <Link
        to="/signin"
        className={
          variant === "desktop"
            ? "inline-flex h-9 px-4 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold shadow-sm hover:bg-primary/90 active:scale-95 transition-all"
            : "w-full h-10 px-4 inline-flex items-center justify-center rounded-xl bg-primary text-primary-foreground text-sm font-bold shadow-sm active:scale-98 transition-all"
        }
      >
        <User className="h-3.5 w-3.5 mr-1.5" />
        Sign In
      </Link>
    );
  }

  const email = user.email || "";
  const displayName =
    (user.user_metadata?.full_name as string) ||
    (user.user_metadata?.name as string) ||
    email.split("@")[0] ||
    "Account";
  const avatarUrl = (user.user_metadata?.avatar_url as string) || (user.user_metadata?.picture as string) || "";
  const initials = displayName.slice(0, 2).toUpperCase();
  const role = isAdmin ? "Administrator" : "Member";

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  if (variant === "mobile") {
    return (
      <div className="space-y-2 p-3 rounded-2xl bg-accent/30 border border-border/70">
        <div className="flex items-center gap-3">
          <div className="relative h-10 w-10 rounded-full overflow-hidden border-2 border-primary/40 bg-primary/10 text-primary flex items-center justify-center shrink-0">
            {avatarUrl && !imgError ? (
              <img
                src={avatarUrl}
                alt={displayName}
                className="h-full w-full object-cover"
                onError={() => setImgError(true)}
              />
            ) : (
              <span className="text-xs font-bold">{initials}</span>
            )}
            {isAdmin && (
              <span
                className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-primary border-2 border-background flex items-center justify-center"
                aria-label="Admin"
              >
                <Shield className="h-2 w-2 text-primary-foreground" />
              </span>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-foreground truncate">{displayName}</p>
            <p className="text-xs text-muted-foreground truncate">{email}</p>
            <span className="inline-block text-[10px] font-semibold uppercase tracking-wider text-primary">
              {role}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 pt-2">
          {isAdmin && (
            <button
              onClick={() => navigate("/admin?tab=overview")}
              className="flex items-center justify-center gap-1.5 p-2 rounded-xl bg-primary/10 text-primary text-xs font-semibold hover:bg-primary/20 transition-colors"
            >
              <LayoutDashboard className="h-3.5 w-3.5" />
              Admin
            </button>
          )}
          <button
            onClick={() => navigate("/bookmarks")}
            className="flex items-center justify-center gap-1.5 p-2 rounded-xl bg-background border border-border text-foreground text-xs font-semibold hover:bg-accent transition-colors"
          >
            <Bookmark className="h-3.5 w-3.5" />
            Bookmarks
          </button>
        </div>

        <button
          onClick={handleSignOut}
          className="w-full flex items-center justify-center gap-2 p-2 rounded-xl text-destructive hover:bg-destructive/10 text-xs font-semibold transition-colors mt-1"
        >
          <LogOut className="h-3.5 w-3.5" />
          Sign Out
        </button>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="h-9 w-9 rounded-full overflow-hidden border-2 border-primary/40 hover:border-primary transition-all shadow-sm flex items-center justify-center bg-gradient-to-br from-primary/20 to-primary/5 text-primary text-xs font-bold relative active:scale-95"
          aria-label="Account menu"
        >
          {avatarUrl && !imgError ? (
            <img
              src={avatarUrl}
              alt={displayName}
              className="h-full w-full object-cover"
              onError={() => setImgError(true)}
            />
          ) : (
            <span>{initials}</span>
          )}
          {isAdmin && (
            <span
              className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-primary border-2 border-background flex items-center justify-center"
              aria-label="Admin"
            >
              <Shield className="h-2 w-2 text-primary-foreground" />
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60 z-[80] rounded-2xl p-2 shadow-2xl">
        <DropdownMenuLabel className="flex flex-col gap-0.5 p-2">
          <span className="text-sm font-semibold text-foreground line-clamp-1">
            {displayName}
          </span>
          <span className="text-[11px] font-normal text-muted-foreground line-clamp-1">
            {email}
          </span>
          <span
            className={`text-[10px] font-semibold mt-0.5 uppercase tracking-wider ${isAdmin ? "text-primary font-bold" : "text-muted-foreground"}`}
          >
            {role}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {context === "admin" && isAdmin ? (
          <>
            <DropdownMenuItem
              onClick={() => navigate("/admin?tab=overview")}
              className="cursor-pointer rounded-xl"
            >
              <LayoutDashboard className="h-4 w-4 mr-2" /> Overview
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => navigate("/admin?tab=articles")}
              className="cursor-pointer rounded-xl"
            >
              <FileText className="h-4 w-4 mr-2" /> Articles
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => navigate("/admin?tab=categories")}
              className="cursor-pointer rounded-xl"
            >
              <FolderTree className="h-4 w-4 mr-2" /> Categories
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => navigate("/admin?tab=comments")}
              className="cursor-pointer rounded-xl"
            >
              <MessageSquare className="h-4 w-4 mr-2" /> Comments
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => navigate("/admin?tab=newsletter")}
              className="cursor-pointer rounded-xl"
            >
              <Mail className="h-4 w-4 mr-2" /> Subscribers
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => navigate("/admin?tab=emails")}
              className="cursor-pointer rounded-xl"
            >
              <Send className="h-4 w-4 mr-2" /> Email log
            </DropdownMenuItem>
          </>
        ) : (
          <>
            <DropdownMenuItem
              onClick={() => navigate("/")}
              className="cursor-pointer rounded-xl"
            >
              <Home className="h-4 w-4 mr-2" /> Home
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => navigate("/bookmarks")}
              className="cursor-pointer rounded-xl"
            >
              <Bookmark className="h-4 w-4 mr-2" /> My Bookmarks
            </DropdownMenuItem>
            <div className="px-1 py-0.5">
              <ReadingHistoryDrawer />
            </div>
            {isAdmin && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => navigate("/admin?tab=overview")}
                  className="cursor-pointer rounded-xl font-semibold text-primary focus:text-primary"
                >
                  <LayoutDashboard className="h-4 w-4 mr-2" /> Admin Dashboard
                </DropdownMenuItem>
              </>
            )}
          </>
        )}

        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleSignOut}
          className="cursor-pointer rounded-xl text-destructive focus:text-destructive font-medium"
        >
          <LogOut className="h-4 w-4 mr-2" /> Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
